import { defineStore } from 'pinia';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { useIdStore } from '@/src/store/id';
import { useFileStore } from './datasets-files';
import { removeFromArray } from '../utils';
import { ModelMetadata } from '../types/model';
import type { RGBColor } from '@kitware/vtk.js/types';
import { vec3 } from 'gl-matrix';
import { StateFile, DatasetType } from '../io/state-file/schema';
import { serializeData } from '../io/state-file/utils';
import vtk from '@kitware/vtk.js/vtk';


import vtkPoints from '@kitware/vtk.js/Common/Core/Points';
import vtkCellArray from '@kitware/vtk.js/Common/Core/CellArray';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import { MODEL_ORGAN_COLORS } from '@/src/constants';

export const defaultModelMetadata = () => ({
  name: '(none)',
  display: true,
  color: [1, 0, 0] as RGBColor,
});

interface State {
  idList: string[]; // list of IDs
  dataIndex: Record<string, vtkPolyData>; // ID -> VTK object
  metadata: Record<string, ModelMetadata>; // ID -> metadata
  renderTrigger: number; // New property to trigger re-render
}
export const useModelStore = defineStore('models', {
  state: (): State => ({
    idList: [],
    dataIndex: Object.create(null),
    metadata: Object.create(null),
    renderTrigger: 0, // Initialize render trigger
  }),
  actions: {
    addVTKPolyData(name: string, polyDatajson: vtkPolyData, newColor?: vec3) {
      const id = useIdStore().nextId();
      this.idList.push(id);

      const polyData = vtk(polyDatajson) as vtkPolyData

      this.dataIndex[id] = polyData;
      this.metadata[id] = { ...defaultModelMetadata(), name };

      const color = newColor || [0,0,1] as vec3;
      this.updateData(id, polyData, color);

      return id;
    },

    createVTKPolyData(name: string, geomPos: number[], geomTrIds: number[], newColor?: vec3) {
      const id = useIdStore().nextId();
      this.idList.push(id);

      // const polyData = vtk(polyDatajson) as vtkPolyData

      const polyData = vtkPolyData.newInstance();
      polyData.getPoints().setData(geomPos);
      polyData.getPolys().setData(geomTrIds);
      polyData.modified();

      this.dataIndex[id] = polyData;
      this.metadata[id] = { ...defaultModelMetadata(), name };

      const color = newColor || vec3.scale([0, 0, 0], MODEL_ORGAN_COLORS[name], 1 / 255) as vec3;
      this.updateData(id, polyData, color);

      return id;
    },

    updateGeometry(name: string, geomPos: number[])
    {
      const targetId = Object.keys(this.metadata).find(id => this.metadata[id].name === name);

      if (targetId) {
        const polyData = this.dataIndex[targetId];
        polyData.getPoints().setData(geomPos);
        polyData.modified();
        this.renderTrigger++;
        // console.log('we are supposed to have updated polydata of "${name}"')
      }
      else
      {
        console.log('id ${id} is not found in dataIndex')
      }
    },

    updateColor(id: string, newColor: vec3)
    {
      this.metadata[id].color = newColor as RGBColor;
    },

    updateData(id: string, polyData: vtkPolyData, newColor: vec3) {
      if (id in this.metadata) {
        const metadata: ModelMetadata = {
          name: this.metadata[id].name,
          display: true,
          color: newColor as RGBColor,
        };

        this.metadata[id] = metadata;
        this.dataIndex[id] = polyData;
      }
      this.dataIndex[id] = polyData;
    },


    deleteData(id: string) {
      if (id in this.dataIndex) {
        delete this.dataIndex[id];
        delete this.metadata[id];
        removeFromArray(this.idList, id);
      }
    },

    async serialize(stateModel: StateFile) {
      const fileStore = useFileStore();
      // We want to filter out volume images (which are generated and don't have
      // input files in fileStore with matching imageID.)
      const dataIDs = this.idList.filter((id) => id in fileStore.byDataID);

      await serializeData(stateModel, dataIDs, DatasetType.MODEL);
    },

  },
});
